// SPDX-License-Identifier: LicenseRef-Proprietary

import React from 'react';
import { ValidationIssue } from '../../lib/validation';

interface ValidationModalProps {
  isOpen: boolean;
  issues: ValidationIssue[];
  onClose: () => void;
  onIssueClick?: (issue: ValidationIssue) => void;
}

const ValidationModal: React.FC<ValidationModalProps> = ({ 
  isOpen, 
  issues, 
  onClose, 
  onIssueClick 
}) => {
  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  const handleIssueClick = (issue: ValidationIssue) => {
    if (onIssueClick) {
      onIssueClick(issue);
    }
    onClose();
  };

  const getSeverityIcon = (severity: 'error' | 'warning') => {
    return severity === 'error' ? '❌' : '⚠️';
  };

  const getSeverityColor = (severity: 'error' | 'warning') => {
    return severity === 'error' ? '#ff4d4f' : '#faad14';
  };

  return (
    <div 
      className="validation-details-backdrop" 
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
      role="dialog"
      aria-modal="true"
      aria-labelledby="validation-modal-title"
    >
      <div className="validation-details-panel">
        <div className="validation-details-header">
          <h4 id="validation-modal-title">Validation Issues</h4>
          <button
            className="validation-modal-close"
            onClick={onClose}
            aria-label="Close validation modal"
          >
            ×
          </button>
        </div>
        
        <div className="validation-issues-list">
          {issues.length === 0 ? (
            <div className="no-issues">
              <p>No validation issues found. Your tree is valid!</p>
            </div>
          ) : (
            <ul>
              {issues.map((issue, index) => (
                <li 
                  key={issue.id} 
                  className={`validation-issue validation-issue--${issue.type} validation-issue-card`}
                  onClick={() => handleIssueClick(issue)}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span 
                      style={{ 
                        fontSize: 'var(--font-size-base)',
                        color: getSeverityColor(issue.severity)
                      }}
                    >
                      {getSeverityIcon(issue.severity)}
                    </span>
                    <div style={{ flex: 1 }}>
                      <div className="validation-issue-card__title">
                        {issue.nodeId ? `Node "${issue.nodeId}"` : 'Tree Structure'}
                      </div>
                      <div className="validation-issue-card__message">
                        {issue.message}
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default ValidationModal;
